import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    createEntityProperty,
    deleteEntityProperty,
    fetchEntityPropertiesRevision,
    updateEntityProperty,
} from '../firebase/firestore/entityPropertyMutations';
import {isRetryableConflict, mapPropertyMutationError,} from '../utils/propertyMutationErrors';

const MAX_CONFLICT_ATTEMPTS = 3;
const INITIAL_BACKOFF_MS = 250;

function sleep(delayMs) {
    return new Promise((resolve) => setTimeout(resolve, delayMs));
}

function toSafeRevision(value) {
    return Number.isInteger(value) && value >= 0 ? value : 0;
}

export function useEntityPropertyMutations({entityId, revision, onMutationSuccess, retryPolicy = {}}) {
    const [activeOperations, setActiveOperations] = useState(0);
    const [error, setError] = useState(null);
    const revisionRef = useRef(toSafeRevision(revision));
    const entityIdRef = useRef(entityId);
    const maxConflictAttempts = Number.isInteger(retryPolicy.maxAttempts)
        ? retryPolicy.maxAttempts
        : MAX_CONFLICT_ATTEMPTS;
    const initialBackoffMs = Number.isInteger(retryPolicy.initialBackoffMs)
        ? retryPolicy.initialBackoffMs
        : INITIAL_BACKOFF_MS;
    const sleepFn = typeof retryPolicy.sleep === 'function' ? retryPolicy.sleep : sleep;
    const fetchRevisionFn = typeof retryPolicy.fetchRevision === 'function'
        ? retryPolicy.fetchRevision
        : fetchEntityPropertiesRevision;

    useEffect(() => {
        if (entityIdRef.current !== entityId) {
            entityIdRef.current = entityId;
            revisionRef.current = toSafeRevision(revision);
            return;
        }

        const nextRevision = toSafeRevision(revision);
        if (activeOperations === 0) {
            revisionRef.current = nextRevision;
        } else {
            revisionRef.current = Math.max(revisionRef.current, nextRevision);
        }
    }, [activeOperations, entityId, revision]);

    useEffect(() => {
        setError(null);
    }, [entityId]);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const runMutationWithRetry = useCallback(async (runner) => {
        setActiveOperations((count) => count + 1);
        setError(null);

        try {
            let attempt = 0;
            let backoffMs = initialBackoffMs;

            while (attempt < maxConflictAttempts) {
                try {
                    const expectedRevision = revisionRef.current;
                    const result = await runner(expectedRevision);

                    revisionRef.current = toSafeRevision(result?.newRevision);
                    onMutationSuccess?.(result);
                    return result;
                } catch (err) {
                    attempt += 1;

                    if (!isRetryableConflict(err) || attempt >= maxConflictAttempts) {
                        const mappedError = mapPropertyMutationError(err, {
                            retriesExhausted: true,
                        });
                        const wrappedError = new Error(mappedError.message);
                        wrappedError.code = mappedError.code;
                        wrappedError.cause = err;
                        setError(wrappedError);
                        throw wrappedError;
                    }

                    const currentRevision = await fetchRevisionFn(entityId);
                    if (Number.isInteger(currentRevision)) {
                        revisionRef.current = currentRevision;
                    }

                    await sleepFn(backoffMs);
                    backoffMs *= 2;
                }
            }

            throw new Error('Unexpected mutation retry state.');
        } finally {
            setActiveOperations((count) => Math.max(0, count - 1));
        }
    }, [entityId, fetchRevisionFn, initialBackoffMs, maxConflictAttempts, onMutationSuccess, sleepFn]);

    const createProperty = useCallback(async (input) => {
        return runMutationWithRetry((expectedRevision) => createEntityProperty({
            entityId,
            expectedRevision,
            property: input,
        }));
    }, [entityId, runMutationWithRetry]);

    const updateProperty = useCallback(async (propertyItemId, patch) => {
        return runMutationWithRetry((expectedRevision) => updateEntityProperty({
            entityId,
            propertyItemId,
            expectedRevision,
            patch,
        }));
    }, [entityId, runMutationWithRetry]);

    const deleteProperty = useCallback(async (propertyItemId) => {
        return runMutationWithRetry((expectedRevision) => deleteEntityProperty({
            entityId,
            propertyItemId,
            expectedRevision,
        }));
    }, [entityId, runMutationWithRetry]);

    return useMemo(() => ({
        createProperty,
        updateProperty,
        deleteProperty,
        saving: activeOperations > 0,
        error,
        clearError,
    }), [activeOperations, clearError, createProperty, deleteProperty, error, updateProperty]);
}
